import {
  Button,
  ButtonProps,
  InteractiveContainer,
  Menu,
  MenuSections,
  PlainButton,
} from "@jugl-web/ui-components";
import {
  Switch,
  SwitchProps,
} from "@jugl-web/ui-components/cross-platform/Switch";
import { cx } from "@jugl-web/utils";
import React from "react";
import { CRMStatus, CRMStatusPill } from "../CRMStatusPill";
import { ReactComponent as MenuIcon } from "./assets/menu.svg";

export interface CRMAccountCardProps {
  title: string;
  description: React.ReactNode;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  children?: React.ReactNode;
  status?: CRMStatus;
  img?: string;
  buttonProps?: Omit<ButtonProps, "ref"> & {
    text: string;
  };
  switchProps?: SwitchProps;
  menuItems?: MenuSections;
  onClick?: () => void;
}

export const CRMAccountCard: React.FC<CRMAccountCardProps> = ({
  img,
  title,
  description,
  status,
  icon,
  buttonProps,
  switchProps,
  menuItems,
  onClick,
  children,
}) => (
  <InteractiveContainer
    className={cx(
      "border-dark-100 flex flex-col gap-6 rounded-3xl border border-solid bg-white p-8 shadow-[0_2px_4px_rgba(0,0,0,0.08)] transition-shadow",
      onClick ? "hover:shadow-[0_8px_16px_rgba(0,0,0,0.08)]" : "cursor-default"
    )}
    onClick={onClick}
  >
    <div className="flex items-center justify-between gap-8">
      <div className="flex w-full min-w-0 items-center gap-6">
        {img ? (
          <div className="relative">
            <div className="h-[72px] w-[72px] shrink-0 overflow-hidden rounded-2xl border border-solid border-[#E0E0E0] bg-[#E0E0E0]">
              <img
                src={img}
                alt={title}
                className="h-full w-full object-cover object-center"
              />
            </div>
            <div className="absolute -bottom-2.5 -right-2.5 h-10 w-10">
              {React.createElement(icon, { className: "w-full h-full" })}
            </div>
          </div>
        ) : (
          <>{React.createElement(icon, { className: "shrink-0" })}</>
        )}
        <div className="flex flex-col gap-1 overflow-hidden">
          <span className="font-secondary truncate text-xl font-medium leading-5">
            {title}
          </span>
          <span className="text-dark-700 font-secondary text-sm leading-[140%]">
            {description}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-4">
        {status && <CRMStatusPill status={status} />}
        {buttonProps && (
          <>
            <div className="bg-dark-100 mx-1 h-10 w-px shrink-0" />
            <Button className="h-10" {...buttonProps}>
              {buttonProps.text}
            </Button>
          </>
        )}
        {switchProps && (
          <div onClick={(e) => e.stopPropagation()}>
            <Switch {...switchProps} />
          </div>
        )}
        {menuItems && (
          <Menu
            placement="bottom"
            renderTrigger={({ Trigger, triggerRef }) => (
              <Trigger
                ref={triggerRef}
                as={PlainButton}
                className="bg-grey-100 hover:bg-grey-200 flex h-10 w-10 items-center justify-center rounded-lg transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <MenuIcon />
              </Trigger>
            )}
            autoClose
            sections={menuItems}
          />
        )}
      </div>
    </div>
    {children}
  </InteractiveContainer>
);
